import {
  Box,
  chakra,
  ComponentMultiStyleConfig,
  HStack,
  Icon,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { Control, useController } from "react-hook-form";
import { FieldPath } from "react-hook-form/dist/types/path/eager";
import { FileUpload } from "../../../file-upload";
import { hasValue } from "../../../../lib/util/common-util";
import { FiLink } from "react-icons/fi";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import { FieldValues } from "react-hook-form/dist/types/fields";

export type FileAttachableTextboxProps<FormValues extends FieldValues> = {
  control: Control<FormValues>;
  textareaFieldPath: FieldPath<FormValues>;
  fileUploadFieldPath: FieldPath<FormValues>;

  placeholder: string | undefined;
  fileUploadLabel: string;
  focusOnMount?: boolean;
} & ComponentStyleProps;

// TODO ここではautofocus属性を用いてオートフォーカスを実現しているが、できればuseAutoFocusを使いたい。
export const FileAttachableTextbox = <FormValues extends FieldValues>({
  control,
  textareaFieldPath,
  fileUploadFieldPath,

  placeholder,
  fileUploadLabel,
  focusOnMount,

  ...rest
}: FileAttachableTextboxProps<FormValues>) => {
  const styles = useMultiStyleConfig("FileAttachableTextboxStyles", {});

  const { field: textareaField, fieldState: textareaFieldState } =
    useController({
      control,
      name: textareaFieldPath,
      rules: { required: false },
    });
  const { field: fileUploadField, fieldState: fileUploadFieldState } =
    useController({
      control,
      name: fileUploadFieldPath,
      rules: { required: false },
    });

  const files = useMemo(() => {
    if (hasValue(fileUploadField.value)) {
      return fileUploadField.value as File[];
    } else {
      return [];
    }
  }, [fileUploadField.value]);

  return (
    <VStack {...rest} align="stretch" spacing="0" __css={styles.container}>
      <chakra.textarea
        placeholder={placeholder}
        ref={textareaField.ref}
        autoFocus={focusOnMount}
        onChange={textareaField.onChange}
        onBlur={textareaField.onBlur}
        value={textareaField.value as string}
        w="100%"
        border="none"
        outline="none"
        boxShadow="none"
        resize="vertical"
        __css={styles.textbox}
      />
      <FileUpload
        onChange={fileUploadField.onChange}
        name={fileUploadField.name}
        accept={undefined}
        multiple={true}
      >
        <chakra.button type="button" w="100%" __css={styles.fileUpload}>
          <VStack spacing={1} align="stretch" textAlign="left">
            {files.length === 0 ? (
              <Box __css={styles.fileUploadMessage}>{fileUploadLabel}</Box>
            ) : null}
            {files.length > 0 &&
              files.map((file, idx) => (
                <HStack key={idx} spacing={1} __css={styles.fileUploadFilename}>
                  <Icon as={FiLink} />
                  <chakra.span>{file.name}</chakra.span>
                </HStack>
              ))}
          </VStack>
        </chakra.button>
      </FileUpload>
    </VStack>
  );
};

export const FileAttachableTextboxStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "textbox",
    "fileUpload",
    "fileUploadMessage",
    "fileUploadFilename",
  ],
  baseStyle: {
    container: {
      overflow: "hidden",

      border: "solid 2px",
      borderColor: "gray.300",
      borderRadius: "lg",
      _focusWithin: {
        borderColor: "blue.500",
      },
    },
    textbox: {
      minH: "100px",
      px: "4",
      py: "2",

      fontSize: "md",
      color: "gray.900",
      _placeholder: {
        color: "gray.500",
      },
    },
    fileUpload: {
      pt: "1",
      pr: "1.5",
      pb: "1.5",
      pl: "2",

      bgColor: "gray.100",
      borderTopWidth: "1px",
      borderColor: "gray.200",
      fontSize: "xs",
    },
    fileUploadMessage: {
      color: "gray.400",
    },
    fileUploadFilename: {
      color: "gray.500",
    },
  },
};
